import type { DuctedPanelSelection } from 'components/equipment-page/ducted-system-detail-panel';
import { SystemOptionCondenserType } from 'data/types';
import useDuctedComponentSwap, {
  DuctedComponentSwapHandler,
  UseDuctedComponentSwapResult
} from 'hooks/use-ducted-component-swap';
import { Fragment, useMemo } from 'react';
import { condenserSlotValues } from 'utils/ducted-component-swap';
import {
  DuctedSwapLockState,
  DuctedSwapSlot
} from 'utils/ducted-component-swap-types';
import { KEPT_EQUIPMENT_PRODUCT_ID } from 'utils/equipment-placeholder';
import {
  BlowerRow,
  CondenserHeader,
  ZoneRow
} from './condenser-component-rows';
import DuctedSlotActions from './ducted-slot-actions';

type DuctedCondenserComponentGroupProps = Readonly<{
  condenser: SystemOptionCondenserType;
  candidates: DuctedPanelSelection[];
  isLoading: boolean;
  onComponentSwap?: DuctedComponentSwapHandler;
  lockState?: DuctedSwapLockState;
}>;

type ProductSlotActionsProps = Readonly<{
  slot: DuctedSwapSlot;
  product: { productId: number; price: number };
  swapModel: UseDuctedComponentSwapResult;
  isLoading: boolean;
}>;

function ProductSlotActions({
  slot,
  product,
  swapModel,
  isLoading
}: ProductSlotActionsProps) {
  if (product.productId === KEPT_EQUIPMENT_PRODUCT_ID) return null;
  return (
    <DuctedSlotActions
      slot={slot}
      currentPrice={product.price}
      swapModel={swapModel}
      isLoading={isLoading}
    />
  );
}

export default function DuctedCondenserComponentGroup({
  condenser,
  candidates,
  isLoading,
  onComponentSwap,
  lockState
}: DuctedCondenserComponentGroupProps) {
  const current = useMemo(() => condenserSlotValues(condenser), [condenser]);
  const swapModel = useDuctedComponentSwap(
    current,
    candidates,
    onComponentSwap,
    lockState
  );
  const zones = condenser.zones.filter((zone) => zone.selectedProduct != null);
  const editable = onComponentSwap !== undefined;

  if (!condenser.selectedProduct) {
    return null;
  }

  return (
    <div className='overflow-hidden rounded-lg border border-gray-200'>
      <CondenserHeader
        condenser={condenser}
        zoneCount={zones.length}
        actions={
          editable && (
            <ProductSlotActions
              slot='outdoor'
              product={condenser.selectedProduct}
              swapModel={swapModel}
              isLoading={isLoading}
            />
          )
        }
      />
      {zones.map((zone) => (
        <Fragment key={zone.uniqueId}>
          <ZoneRow
            zone={zone}
            actions={
              editable && (
                <ProductSlotActions
                  slot='indoor'
                  product={zone.selectedProduct}
                  swapModel={swapModel}
                  isLoading={isLoading}
                />
              )
            }
          />
          {zone.blowerProduct && (
            <BlowerRow
              blower={zone.blowerProduct}
              actions={
                editable && (
                  <ProductSlotActions
                    slot='blower'
                    product={zone.blowerProduct}
                    swapModel={swapModel}
                    isLoading={isLoading}
                  />
                )
              }
            />
          )}
        </Fragment>
      ))}
    </div>
  );
}
